import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { AnimTap2, EColors2, S_IncreaseClick } from 'libs/styles/styling';
import { getExternalLinkLocalization } from 'libs/utils/logic/ui/languageLogic';
import { useAnalytics } from 'libs/utils/site/analytics';
import { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { CtxEventView } from 'session/ctxEventView';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    color: string;
    className?: string;
}

interface Item {
    title: string;
    subtitle?: string;
    external?: boolean;
    iconSet: IconPrefix;
    iconName: IconName;
    linkTo: string;
    itemName?: string;
}

export const MobileMenu = (props: Props): JSX.Element => {
    const ref = useRef<HTMLDivElement>(null);
    const ctxEventView = useContext(CtxEventView);
    const { eventData, eventPath } = ctxEventView;
    const navigate = useNavigate();
    const analytics = useAnalytics();

    useEffect(() => {
        ctxEventView.setMobileMenuOpen(false);
    }, []);

    const order = ctxEventView?.eventData.eventInfo.order.wide;

    const items: Item[] = order.map((item) => {
        if (item === 'home') {
            return {
                title: Localization.HOME,
                iconSet: 'far',
                iconName: 'home',
                linkTo: `/event/${eventPath}`,
                itemName: item
            };
        } else if (item === 'map') {
            return {
                title: Localization.MAP,
                iconSet: 'far',
                iconName: 'location-dot',
                linkTo: `/event/${eventPath}/map`,
                itemName: item
            };
        } else if (item === 'program') {
            return {
                title: ctxEventView.programTitleShort,
                iconSet: 'far',
                iconName: 'clock',
                linkTo: `/event/${eventPath}/program`,
                itemName: item
            };
        } else if (item === 'information') {
            return {
                title: Localization.INFO,
                iconSet: 'far',
                iconName: 'info-circle',
                linkTo: `/event/${eventPath}/information`,
                itemName: item
            };
        } else {
            const otherEntities = ctxEventView.eventData.eventInfo.otherEntities;
            const find = otherEntities.find((entity) => entity.id === item);
            if (find) {
                return {
                    title: find.name,
                    iconSet: 'far',
                    iconName: find.icon,
                    linkTo: `/event/${eventPath}/${find.id}`,
                    itemName: find.name
                };
            }
        }
    });

    eventData?.eventInfo?.externalLinks?.forEach((link) => {
        items.push({
            title: getExternalLinkLocalization(link.title),
            subtitle: Localization.EXTERNAL_LINK,
            iconSet: 'far',
            iconName: 'arrow-up-right-from-square',
            external: true,
            linkTo: link.url
        });
    });

    function handleClick(item: Item) {
        if (item.external) {
            analytics.track('event_ui.external_link', {
                source: 'burger_menu',
                destination: item.linkTo
            });
            window.open(item.linkTo, '_blank');
        } else {
            analytics.track('event_ui.navigate.clicked', {
                source: 'burger_menu',
                destination: item.itemName
            });
            ctxEventView.setScrollToTop(true);
            ctxEventView.setMobileMenuOpen(false);
            navigate(item.linkTo);
        }
    }

    return (
        <S_MobileMenu className={props.className}>
            <S_IconWrapper
                onClick={() => {
                    ctxEventView.setMobileMenuOpen(true);
                }}
                {...AnimTap2}
            >
                <S_Icon $color={props.color} icon={['far', 'bars']} />
            </S_IconWrapper>
            <AnimatePresence>
                {ctxEventView.mobileMenuOpen && (
                    <S_Open
                        ref={ref}
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.2 }}
                        exit={{ scale: 0 }}
                    >
                        <S_Close
                            onClick={() => {
                                ctxEventView.setMobileMenuOpen(false);
                            }}
                        >
                            <S_Icon $color={EColors2.DARK} icon={['far', 'xmark']} />
                        </S_Close>

                        <S_Items>
                            {items.map((item) => {
                                if (!item) {
                                    return <></>;
                                }
                                return (
                                    <S_Item
                                        key={item.title}
                                        onClick={() => {
                                            handleClick(item);
                                        }}
                                    >
                                        {item.iconName && (
                                            <S_MenuIcon icon={[item.iconSet, item.iconName]} />
                                        )}
                                        <S_Right>
                                            <S_MenuText>{item.title}</S_MenuText>
                                            {item.subtitle && (
                                                <S_Subtitle>{item.subtitle}</S_Subtitle>
                                            )}
                                        </S_Right>
                                    </S_Item>
                                );
                            })}
                        </S_Items>
                    </S_Open>
                )}
            </AnimatePresence>
        </S_MobileMenu>
    );
};

const S_MobileMenu = styled.div``;

const S_Open = styled(motion.div)`
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100vh;
    z-index: 9999999999;
    display: flex;
    background-color: white;
    flex-direction: column;
`;

const S_Close = styled.div`
    height: 64px;
    display: flex;
    justify-content: flex-end;
    padding: 0 23px 0 20px;
    width: 100%;
    align-items: center;
    position: relative;
    &:before {
        position: absolute;
        content: '';
        top: -10px;
        right: -10px;
        left: -10px;
        bottom: -10px;
        cursor: pointer;
    }
`;

const S_Icon = styled(FontAwesomeIcon)<{ $color: string }>`
    color: ${(props) => props.$color};
    font-size: 20px;
`;

const S_IconWrapper = styled(motion(S_IncreaseClick))`
    margin: 2px 0 0 0;
`;

const S_Items = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 0 20px;
    overflow-y: auto;
`;

const S_Item = styled(motion.div)`
    display: flex;
    align-items: center;
    height: 64px;
    border-bottom: 1px solid #e6e6e6;
    flex-shrink: 0;
`;

const S_MenuIcon = styled(FontAwesomeIcon)`
    font-size: 20px;
    margin-right: 20px;
`;

const S_MenuText = styled.div`
    font-size: 16px;
    font-weight: 500;
    color: #000000;
`;

const S_Right = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex: 1;
`;

const S_Subtitle = styled.div`
    font-size: 12px;
    font-weight: 400;
    color: #000000;
    margin-top: 4px;
`;
